import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildVbpGrowthChartPoints,
  resolveVbpGrowthChartEndYearMo,
} from '@act/shared/data-transfer-objects';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import {
  body,
  CORAL,
  foreground,
  muted,
  NAVY,
} from './tokens';

type FunnelStep = { label: string; value: number };

const DEFAULT_FUNNEL: FunnelStep[] = [
  { label: 'VBP eligible', value: 3568 },
  { label: 'VBP assigned', value: 1580 },
  { label: 'VBP engaged', value: 1124 },
];

const FUNNEL_BAR_COLORS = [NAVY, '#FF9C8B', CORAL] as const;

const formatCount = (value: number): string => value.toLocaleString('en-US');

const buildFunnelSteps = (data: Record<string, unknown>): FunnelStep[] => {
  if (!Array.isArray(data.funnel)) {
    return DEFAULT_FUNNEL;
  }
  const steps = data.funnel as FunnelStep[];
  return steps.length > 0 ? steps : DEFAULT_FUNNEL;
};

/**
 * VBP engagement section: eligible/engaged counts, optional growth trend, and funnel bars.
 * @param props - Resolved vbp-engagement snapshot from warehouse or mocks.
 */
const VbpEngagement = ({
  data,
  reportingPeriod,
}: ReportHostedSectionProps) => {
  const engaged =
    typeof data.vbpEngaged === 'number'
      ? data.vbpEngaged
      : (buildFunnelSteps(data).at(-1)?.value ?? 1124);
  const eligible =
    typeof data.vbpEligible === 'number'
      ? data.vbpEligible
      : (buildFunnelSteps(data)[0]?.value ?? 3568);
  const ratePct =
    typeof data.engagementRatePct === 'number'
      ? data.engagementRatePct
      : eligible > 0
        ? Math.round((engaged / eligible) * 100)
        : 0;

  const funnel = buildFunnelSteps(data);
  const maxFunnel = funnel[0]?.value ?? 1;
  const growthValues = Array.isArray(data.sixMonthGrowth)
    ? (data.sixMonthGrowth as number[])
    : [];
  const measurementPeriod =
    typeof data.measurementPeriod === 'string' ? data.measurementPeriod : null;
  const endYearMo = resolveVbpGrowthChartEndYearMo(
    reportingPeriod,
    measurementPeriod,
  );
  const growth = buildVbpGrowthChartPoints(growthValues, endYearMo);
  const printStatic = useReportsPrintStaticLayout();

  return (
    <HostedReportSectionLayout
      sectionDomId="vbp-engagement"
      className="section--white report-hosted-section-body"
      style={{ fontFamily: body }}
      banner={
        <SectionBanner title="VBP Engagement" iconHref="#icon_medical_health" />
      }
    >
      <SectionReveal delay={0.1}>
        <div
          className="stat-grid stat-grid--4"
          style={{
            gridTemplateColumns: 'repeat(2, 1fr)',
            marginBottom: '4rem',
          }}
        >
          <div className="stat-card stat-card--center">
            <div className="stat-num stat-num--md">{formatCount(engaged)}</div>
            <div className="stat-label">Currently engaged</div>
          </div>
          <div className="stat-card stat-card--center">
            <div className="stat-num stat-num--md">{ratePct}%</div>
            <div className="stat-label">VBP-eligible engagement rate</div>
          </div>
        </div>

        <div
          className="vbp-engagement-charts two-col two-col--equal"
          style={{ marginBottom: '4rem', gap: '1.5rem' }}
        >
          {growth.length > 0 ? (
            <div className="chart-card">
              <div className="chart-card__title">Engagement growth</div>
              <div className="chart-card__sub">
                Net engaged members, last {growth.length} months
              </div>
              <div className="chart-card__body report-chart-host" style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={growth}
                    margin={
                      printStatic
                        ? { top: 4, right: 6, bottom: 0, left: -10 }
                        : { top: 8, right: 12, bottom: 0, left: 0 }
                    }
                  >
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: printStatic ? 8 : 11, fill: muted }}
                      interval={0}
                      angle={printStatic ? -22 : -30}
                      textAnchor="end"
                      height={printStatic ? 40 : 56}
                    />
                    <YAxis
                      tick={{ fontSize: printStatic ? 8 : 11, fill: muted }}
                      width={printStatic ? 34 : 48}
                    />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="engaged"
                      stroke={CORAL}
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: '#fff', stroke: CORAL, strokeWidth: 2.5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          <div className="chart-card">
            <div className="chart-card__title">Reaching the eligible population</div>
            <div className="chart-card__sub">Where members are today</div>
            <div className="chart-card__body vbp-funnel-chart">
              {funnel.map((step, index) => {
                const widthPct =
                  maxFunnel > 0 ? (step.value / maxFunnel) * 100 : 0;
                const barColor =
                  FUNNEL_BAR_COLORS[index] ??
                  FUNNEL_BAR_COLORS[FUNNEL_BAR_COLORS.length - 1];
                return (
                  <div key={step.label} className="vbp-funnel-row">
                    <span
                      className="vbp-funnel-row__label"
                      style={{ color: foreground }}
                    >
                      {step.label}
                    </span>
                    <div
                      className="vbp-funnel-row__bar"
                      style={{
                        width: `${Math.max(widthPct, 4)}%`,
                        background: barColor,
                      }}
                    />
                    <span
                      className="vbp-funnel-row__value"
                      style={{ color: muted }}
                    >
                      {formatCount(step.value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default VbpEngagement;
